import { useState, useEffect, useCallback } from 'react';
import {
    ArrowLeft, Plus, Search, User, Bot, Zap, Link2, Unlink, Mail,
    Building, UserPlus, Users, GitBranch, Send, LogOut, AlertTriangle,
    CheckCircle, Clock, Shield
} from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';
import { useReplica } from '../contexts/ReplicaContext';
import { useTranslation } from 'react-i18next';
import AccessibleModal from './AccessibleModal';
import { getApiUrl } from '../lib/env';
import type { Employee, EmployeeLinkType } from '../types/api';

// ============================================================================
// TYPES
// ============================================================================

interface EmployeeLinkingViewProps {
    onNavigate: (view: string) => void;
}

type NodeStateFilter = 'all' | 'HUMAN' | 'AUGMENTED' | 'AUTONOMOUS';
type TabView = 'directory' | 'invited';

function getNodeState(employee: Employee): 'HUMAN' | 'AUGMENTED' | 'AUTONOMOUS' {
    if (!employee.replicaLink) return 'HUMAN';
    if (employee.replicaLink.linkType === 'AUTONOMOUS') return 'AUTONOMOUS';
    return 'AUGMENTED';
}

const API_BASE = getApiUrl();

// ============================================================================
// API HELPERS
// ============================================================================

async function getToken(user: { getIdToken?: () => Promise<string> } | null): Promise<string | null> {
    if (!user?.getIdToken) return null;
    try { return await user.getIdToken(); } catch { return null; }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function EmployeeLinkingView({ onNavigate }: EmployeeLinkingViewProps) {
    const { currentWorkspace } = useWorkspace();
    const { user } = useAuth();
    const { userReplicas } = useReplica();
    const { t } = useTranslation('dashboard');

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterState, setFilterState] = useState<NodeStateFilter>('all');
    const [filterDept, setFilterDept] = useState('all');
    const [activeTab, setActiveTab] = useState<TabView>('directory');

    // Modals
    const [showAddModal, setShowAddModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [showOffboardModal, setShowOffboardModal] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);

    // Add employee form
    const [newName, setNewName] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newTitle, setNewTitle] = useState('');
    const [newDepartment, setNewDepartment] = useState('');

    // Invite form
    const [inviteName, setInviteName] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteTitle, setInviteTitle] = useState('');
    const [inviteDept, setInviteDept] = useState('');
    const [inviteRole, setInviteRole] = useState<'collaborator' | 'viewer'>('collaborator');
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteSuccess, setInviteSuccess] = useState('');

    // Offboard
    const [offboardLoading, setOffboardLoading] = useState(false);

    // ── Fetch employees ─────────────────────────────────────────────────
    const fetchEmployees = useCallback(async () => {
        try {
            const token = await getToken(user);
            if (!token) return;
            const res = await fetch(`${API_BASE}/employees`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                if (data.employees) setEmployees(data.employees);
            }
        } catch { /* keep existing */ }
    }, [user]);

    useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

    // ── Derived ─────────────────────────────────────────────────────────
    const activeEmployees = employees.filter(e => e.status === 'ACTIVE' || e.status === 'INVITED');
    const departments = [...new Set(activeEmployees.map(e => e.department).filter(Boolean))] as string[];
    const stats = {
        total: activeEmployees.length,
        human: activeEmployees.filter(e => getNodeState(e) === 'HUMAN').length,
        augmented: activeEmployees.filter(e => getNodeState(e) === 'AUGMENTED').length,
        autonomous: activeEmployees.filter(e => getNodeState(e) === 'AUTONOMOUS').length,
        invited: employees.filter(e => e.status === 'INVITED').length,
        sponsored: employees.filter(e => e.replicaLink?.sponsorStatus === 'ACTIVE').length,
    };

    const filtered = activeEmployees.filter(e => {
        if (activeTab === 'invited' && e.status !== 'INVITED') return false;
        if (activeTab === 'directory' && e.status === 'INVITED') return true; // show all in directory
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            if (!e.name.toLowerCase().includes(q) && !e.email?.toLowerCase().includes(q) && !e.title?.toLowerCase().includes(q)) return false;
        }
        if (filterState !== 'all' && getNodeState(e) !== filterState) return false;
        if (filterDept !== 'all' && e.department !== filterDept) return false;
        return true;
    });

    // ── Handlers ────────────────────────────────────────────────────────

    const handleAddEmployee = async () => {
        if (!newName.trim()) return;
        try {
            const token = await getToken(user);
            if (token) {
                const res = await fetch(`${API_BASE}/employees`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: newName, email: newEmail || undefined, title: newTitle || undefined, department: newDepartment || undefined }),
                });
                if (res.ok) {
                    await fetchEmployees();
                    resetAddForm();
                    return;
                }
            }
        } catch { /* fallback */ }
        // Local fallback
        const newEmployee: Employee = {
            id: `new-${Date.now()}`, companyId: 'local', name: newName, email: newEmail || undefined,
            title: newTitle || undefined, department: newDepartment || undefined,
            status: 'ACTIVE', createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
        };
        setEmployees(prev => [...prev, newEmployee]);
        resetAddForm();
    };

    const handleInvite = async () => {
        if (!inviteName.trim() || !inviteEmail.trim()) return;
        setInviteLoading(true);
        setInviteSuccess('');
        try {
            const token = await getToken(user);
            if (token) {
                const res = await fetch(`${API_BASE}/employees/invite`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        name: inviteName, email: inviteEmail,
                        title: inviteTitle || undefined, department: inviteDept || undefined,
                        role: inviteRole,
                    }),
                });
                const data = await res.json();
                if (res.ok) {
                    setInviteSuccess(data.message || 'Invitation sent!');
                    await fetchEmployees();
                    setTimeout(() => { resetInviteForm(); }, 2000);
                } else {
                    setInviteSuccess(`Error: ${data.error}`);
                }
            }
        } catch {
            setInviteSuccess('Failed to send invitation.');
        }
        setInviteLoading(false);
    };

    const handleLinkReplica = async (replicaId: string, linkType: EmployeeLinkType = 'AUGMENTED') => {
        if (!selectedEmployee) return;
        try {
            const token = await getToken(user);
            if (token) {
                const res = await fetch(`${API_BASE}/employees/${selectedEmployee.id}/link-replica`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ replicaId, linkType }),
                });
                if (res.ok) {
                    await fetchEmployees();
                    setShowLinkModal(false);
                    setSelectedEmployee(null);
                    return;
                }
            }
        } catch { /* fallback */ }
        // Local fallback
        setEmployees(prev => prev.map(e =>
            e.id === selectedEmployee.id
                ? { ...e, replicaLink: { id: `link-${Date.now()}`, employeeId: e.id, replicaId, linkType } }
                : e
        ));
        setShowLinkModal(false);
        setSelectedEmployee(null);
    };

    const handleUnlink = async (employeeId: string) => {
        try {
            const token = await getToken(user);
            if (token) {
                await fetch(`${API_BASE}/employees/${employeeId}/unlink-replica`, {
                    method: 'DELETE',
                    headers: { Authorization: `Bearer ${token}` },
                });
                await fetchEmployees();
                return;
            }
        } catch { /* fallback */ }
        setEmployees(prev => prev.map(e =>
            e.id === employeeId ? { ...e, replicaLink: undefined } : e
        ));
    };

    const handleOffboard = async () => {
        if (!selectedEmployee) return;
        setOffboardLoading(true);
        try {
            const token = await getToken(user);
            if (token) {
                const res = await fetch(`${API_BASE}/employees/${selectedEmployee.id}/offboard`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (res.ok) {
                    await fetchEmployees();
                }
            }
        } catch { /* continue */ }
        setOffboardLoading(false);
        setShowOffboardModal(false);
        setSelectedEmployee(null);
    };

    const resetAddForm = () => {
        setNewName(''); setNewEmail(''); setNewTitle(''); setNewDepartment('');
        setShowAddModal(false);
    };

    const resetInviteForm = () => {
        setInviteName(''); setInviteEmail(''); setInviteTitle(''); setInviteDept('');
        setInviteRole('collaborator'); setInviteSuccess('');
        setShowInviteModal(false);
    };

    // ════════════════════════════════════════════════════════════════════
    // RENDER
    // ════════════════════════════════════════════════════════════════════

    return (
        <div className="max-w-6xl mx-auto pb-12">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <button onClick={() => onNavigate('dashboard')} className="p-2 text-hig-text-tertiary hover:text-hig-text-primary hover:bg-hig-bg-elevated rounded-lg transition-colors">
                        <ArrowLeft size={18} />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-hig-text-primary flex items-center gap-2">
                            <UserPlus size={22} className="text-emerald-400" />
                            {t('company_dashboard.employee_accounts')}
                        </h1>
                        <p className="text-sm text-hig-text-secondary">
                            {t('company_dashboard.invite_link_employees')} — {currentWorkspace?.name || 'Company'}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <button onClick={() => onNavigate('org-chart')}
                        className="flex items-center gap-2 px-4 py-2 bg-hig-bg-secondary border border-hig-divider text-hig-text-secondary rounded-lg hover:bg-hig-bg-elevated transition-colors text-sm">
                        <GitBranch size={16} /> {t('company_dashboard.team_hierarchy')}
                    </button>
                    <button onClick={() => setShowInviteModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg hover:bg-emerald-500/20 transition-colors text-sm font-medium">
                        <Send size={16} /> Invite Employee
                    </button>
                    <button onClick={() => setShowAddModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 rounded-lg hover:bg-cyan-500/20 transition-colors text-sm font-medium">
                        <Plus size={16} /> Add Employee
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-6 gap-3 mb-6">
                {[
                    { label: 'Total', value: stats.total, icon: <Users size={16} />, color: 'text-hig-text-secondary', bg: 'bg-hig-bg-tertiary' },
                    { label: 'Human', value: stats.human, icon: <User size={16} />, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                    { label: 'Augmented', value: stats.augmented, icon: <Zap size={16} />, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
                    { label: 'Autonomous', value: stats.autonomous, icon: <Bot size={16} />, color: 'text-purple-400', bg: 'bg-purple-500/10' },
                    { label: 'Invited', value: stats.invited, icon: <Mail size={16} />, color: 'text-amber-400', bg: 'bg-amber-500/10' },
                    { label: 'Sponsored', value: stats.sponsored, icon: <Shield size={16} />, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                ].map(s => (
                    <div key={s.label} className="card p-3 flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-xl ${s.bg} flex items-center justify-center ${s.color}`}>{s.icon}</div>
                        <div>
                            <p className="text-lg font-bold text-hig-text-primary">{s.value}</p>
                            <p className="text-[10px] uppercase tracking-wider text-hig-text-tertiary font-semibold">{s.label}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 mb-4 border-b border-hig-divider">
                <button
                    onClick={() => setActiveTab('directory')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'directory'
                            ? 'border-hig-accent text-hig-accent'
                            : 'border-transparent text-hig-text-tertiary hover:text-hig-text-secondary'
                    }`}
                >
                    <Users size={14} className="inline mr-1.5" /> All Employees
                </button>
                <button
                    onClick={() => setActiveTab('invited')}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'invited'
                            ? 'border-amber-400 text-amber-400'
                            : 'border-transparent text-hig-text-tertiary hover:text-hig-text-secondary'
                    }`}
                >
                    <Mail size={14} className="inline mr-1.5" /> Pending Invites
                    {stats.invited > 0 && (
                        <span className="ml-1.5 px-1.5 py-0.5 text-[10px] font-bold bg-amber-500/20 text-amber-400 rounded-full">{stats.invited}</span>
                    )}
                </button>
            </div>

            {/* Search & Filters */}
            <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-hig-text-tertiary" />
                    <input
                        type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                        placeholder="Search employees..."
                        className="w-full pl-10 pr-3 py-2.5 bg-hig-bg-secondary border border-hig-divider rounded-xl text-sm text-hig-text-primary placeholder-hig-text-tertiary focus:outline-none focus:border-hig-accent/50"
                    />
                </div>
                <select value={filterState} onChange={e => setFilterState(e.target.value as NodeStateFilter)}
                    className="px-3 py-2.5 bg-hig-bg-secondary border border-hig-divider rounded-xl text-sm text-hig-text-primary focus:outline-none">
                    <option value="all">All States</option>
                    <option value="HUMAN">Human Only</option>
                    <option value="AUGMENTED">Augmented</option>
                    <option value="AUTONOMOUS">Autonomous</option>
                </select>
                <select value={filterDept} onChange={e => setFilterDept(e.target.value)}
                    className="px-3 py-2.5 bg-hig-bg-secondary border border-hig-divider rounded-xl text-sm text-hig-text-primary focus:outline-none">
                    <option value="all">All Departments</option>
                    {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
            </div>

            {/* Employee Directory */}
            <div className="card overflow-hidden">
                <div className="divide-y divide-hig-divider">
                    {filtered.length === 0 ? (
                        <div className="p-12 text-center">
                            <Users size={40} className="text-hig-text-tertiary mx-auto mb-4" />
                            <h3 className="text-lg font-semibold text-hig-text-primary mb-2">
                                {activeTab === 'invited' ? 'No pending invitations' : 'No employees found'}
                            </h3>
                            <p className="text-sm text-hig-text-secondary mb-4">
                                {activeTab === 'invited'
                                    ? 'Invite employees to join your company workspace.'
                                    : 'Add employees or invite them to start building your augmented workforce.'}
                            </p>
                            <button onClick={() => activeTab === 'invited' ? setShowInviteModal(true) : setShowAddModal(true)}
                                className="btn-primary px-5 py-2 rounded-lg text-sm">
                                <Plus size={16} className="mr-2" /> {activeTab === 'invited' ? 'Invite Employee' : 'Add First Employee'}
                            </button>
                        </div>
                    ) : (
                        filtered.map(employee => {
                            const state = getNodeState(employee);
                            const isSponsored = employee.replicaLink?.sponsorStatus === 'ACTIVE';
                            const isInvited = employee.status === 'INVITED';

                            return (
                                <div key={employee.id} className="px-5 py-4 flex items-center gap-4 hover:bg-hig-bg-elevated transition-colors">
                                    {/* Avatar */}
                                    <div className="relative flex-shrink-0">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold ${
                                            isInvited ? 'bg-amber-600/80' :
                                            state === 'HUMAN' ? 'bg-slate-600' :
                                            state === 'AUGMENTED' ? 'bg-gradient-to-br from-cyan-500 to-blue-600' :
                                            'bg-gradient-to-br from-purple-500 to-indigo-600'
                                        }`}>
                                            {isInvited ? <Mail size={20} /> :
                                             state === 'AUTONOMOUS' ? <Bot size={22} /> : employee.name.charAt(0)}
                                        </div>
                                        {state === 'AUGMENTED' && !isInvited && (
                                            <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-hig-bg-primary border-2 border-cyan-400/60 flex items-center justify-center">
                                                <Bot size={10} className="text-cyan-400" />
                                            </div>
                                        )}
                                    </div>

                                    {/* Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-0.5">
                                            <p className="font-semibold text-hig-text-primary truncate">{employee.name}</p>
                                            {/* State badge */}
                                            {isInvited ? (
                                                <span className="text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase bg-amber-500/10 text-amber-400 border-amber-500/20">
                                                    Invited
                                                </span>
                                            ) : (
                                                <span className={`text-[10px] px-2 py-0.5 rounded-full border font-semibold uppercase ${
                                                    state === 'HUMAN' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                                                    state === 'AUGMENTED' ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20' :
                                                    'bg-purple-500/10 text-purple-400 border-purple-500/20'
                                                }`}>
                                                    {state === 'HUMAN' ? 'Human' : state === 'AUGMENTED' ? 'Augmented' : 'Autonomous'}
                                                </span>
                                            )}
                                            {/* Sponsorship badge */}
                                            {isSponsored && (
                                                <span className="text-[10px] px-2 py-0.5 rounded-full border font-medium bg-emerald-500/10 text-emerald-400 border-emerald-500/20 flex items-center gap-1">
                                                    <Shield size={9} /> Company Sponsored
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-hig-text-secondary">
                                            {employee.title && <span>{employee.title}</span>}
                                            {employee.department && (
                                                <>
                                                    <span className="text-hig-divider">·</span>
                                                    <span className="flex items-center gap-1"><Building size={11} /> {employee.department}</span>
                                                </>
                                            )}
                                            {employee.email && (
                                                <>
                                                    <span className="text-hig-divider">·</span>
                                                    <span className="flex items-center gap-1"><Mail size={11} /> {employee.email}</span>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {!isInvited && (
                                            <>
                                                {employee.replicaLink ? (
                                                    <button onClick={() => handleUnlink(employee.id)}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-500/20 text-red-400 text-xs font-medium hover:bg-red-500/10 transition-colors">
                                                        <Unlink size={13} /> Unlink
                                                    </button>
                                                ) : (
                                                    <button onClick={() => { setSelectedEmployee(employee); setShowLinkModal(true); }}
                                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cyan-500/20 text-cyan-400 text-xs font-medium hover:bg-cyan-500/10 transition-colors">
                                                        <Link2 size={13} /> Link Replica
                                                    </button>
                                                )}
                                                <button onClick={() => { setSelectedEmployee(employee); setShowOffboardModal(true); }}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-hig-divider text-hig-text-tertiary text-xs font-medium hover:bg-red-500/10 hover:text-red-400 hover:border-red-500/20 transition-colors"
                                                    title="Offboard employee">
                                                    <LogOut size={13} />
                                                </button>
                                            </>
                                        )}
                                        {isInvited && (
                                            <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-400">
                                                <Clock size={13} /> Pending
                                            </span>
                                        )}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* ══════ Add Employee Modal ══════ */}
            <AccessibleModal open={showAddModal} onClose={resetAddForm} title="Add Employee"
                panelClassName="bg-hig-bg-secondary rounded-2xl p-6 w-full max-w-md border border-hig-divider">
                <h2 className="text-xl font-bold text-hig-text-primary mb-1">Add Employee</h2>
                <p className="text-sm text-hig-text-secondary mb-5">Add a team member to your org chart</p>
                <div className="space-y-4">
                    <div>
                        <label className="block text-xs text-hig-text-tertiary font-semibold uppercase tracking-wider mb-1">Name *</label>
                        <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Full name"
                            className="w-full px-3 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-hig-text-primary placeholder-hig-text-tertiary text-sm focus:outline-none focus:border-hig-accent/50" />
                    </div>
                    <div>
                        <label className="block text-xs text-hig-text-tertiary font-semibold uppercase tracking-wider mb-1">Email</label>
                        <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="employee@company.com"
                            className="w-full px-3 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-hig-text-primary placeholder-hig-text-tertiary text-sm focus:outline-none focus:border-hig-accent/50" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-hig-text-tertiary font-semibold uppercase tracking-wider mb-1">Title</label>
                            <input type="text" value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="e.g., CTO"
                                className="w-full px-3 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-hig-text-primary placeholder-hig-text-tertiary text-sm focus:outline-none focus:border-hig-accent/50" />
                        </div>
                        <div>
                            <label className="block text-xs text-hig-text-tertiary font-semibold uppercase tracking-wider mb-1">Department</label>
                            <input type="text" value={newDepartment} onChange={e => setNewDepartment(e.target.value)} placeholder="e.g., Engineering"
                                className="w-full px-3 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-hig-text-primary placeholder-hig-text-tertiary text-sm focus:outline-none focus:border-hig-accent/50" />
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={resetAddForm} className="px-4 py-2 text-hig-text-secondary hover:text-hig-text-primary text-sm transition-colors">Cancel</button>
                    <button onClick={handleAddEmployee} disabled={!newName.trim()}
                        className="px-5 py-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 rounded-xl text-sm font-semibold disabled:opacity-40 transition-colors">
                        Add Employee
                    </button>
                </div>
            </AccessibleModal>

            {/* ══════ Invite Employee Modal ══════ */}
            <AccessibleModal open={showInviteModal} onClose={resetInviteForm} title="Invite Employee"
                panelClassName="bg-hig-bg-secondary rounded-2xl p-6 w-full max-w-md border border-hig-divider">
                <h2 className="text-xl font-bold text-hig-text-primary mb-1 flex items-center gap-2">
                    <Send size={20} className="text-emerald-400" /> Invite Employee
                </h2>
                <p className="text-sm text-hig-text-secondary mb-1">Send an invitation — the company sponsors their replica.</p>
                <p className="text-xs text-hig-text-tertiary mb-5 flex items-center gap-1.5">
                    <Shield size={12} className="text-emerald-400" />
                    If they leave, they keep their replica. It goes dormant until they reactivate it.
                </p>

                {inviteSuccess && (
                    <div className={`mb-4 p-3 rounded-lg text-sm flex items-center gap-2 ${
                        inviteSuccess.startsWith('Error') ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                    }`}>
                        {inviteSuccess.startsWith('Error') ? <AlertTriangle size={14} /> : <CheckCircle size={14} />}
                        {inviteSuccess}
                    </div>
                )}

                <div className="space-y-4">
                    <div>
                        <label className="block text-xs text-hig-text-tertiary font-semibold uppercase tracking-wider mb-1">Name *</label>
                        <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Full name"
                            className="w-full px-3 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-hig-text-primary placeholder-hig-text-tertiary text-sm focus:outline-none focus:border-hig-accent/50" />
                    </div>
                    <div>
                        <label className="block text-xs text-hig-text-tertiary font-semibold uppercase tracking-wider mb-1">Email *</label>
                        <input type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="employee@company.com"
                            className="w-full px-3 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-hig-text-primary placeholder-hig-text-tertiary text-sm focus:outline-none focus:border-hig-accent/50" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs text-hig-text-tertiary font-semibold uppercase tracking-wider mb-1">Title</label>
                            <input type="text" value={inviteTitle} onChange={e => setInviteTitle(e.target.value)} placeholder="e.g., CTO"
                                className="w-full px-3 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-hig-text-primary placeholder-hig-text-tertiary text-sm focus:outline-none focus:border-hig-accent/50" />
                        </div>
                        <div>
                            <label className="block text-xs text-hig-text-tertiary font-semibold uppercase tracking-wider mb-1">Department</label>
                            <input type="text" value={inviteDept} onChange={e => setInviteDept(e.target.value)} placeholder="e.g., Engineering"
                                className="w-full px-3 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-hig-text-primary placeholder-hig-text-tertiary text-sm focus:outline-none focus:border-hig-accent/50" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs text-hig-text-tertiary font-semibold uppercase tracking-wider mb-1">Workspace Role</label>
                        <select value={inviteRole} onChange={e => setInviteRole(e.target.value as 'collaborator' | 'viewer')}
                            className="w-full px-3 py-2.5 bg-hig-bg-tertiary border border-hig-divider rounded-xl text-hig-text-primary text-sm focus:outline-none focus:border-hig-accent/50">
                            <option value="collaborator">Collaborator — can manage replicas</option>
                            <option value="viewer">Viewer — read-only access</option>
                        </select>
                    </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                    <button onClick={resetInviteForm} className="px-4 py-2 text-hig-text-secondary hover:text-hig-text-primary text-sm transition-colors">Cancel</button>
                    <button onClick={handleInvite} disabled={!inviteName.trim() || !inviteEmail.trim() || inviteLoading}
                        className="px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-xl text-sm font-semibold disabled:opacity-40 transition-colors flex items-center gap-2">
                        {inviteLoading ? <Clock size={14} className="animate-spin" /> : <Send size={14} />}
                        {inviteLoading ? 'Sending...' : 'Send Invitation'}
                    </button>
                </div>
            </AccessibleModal>

            {/* ══════ Offboard Confirmation Modal ══════ */}
            <AccessibleModal open={showOffboardModal} onClose={() => { setShowOffboardModal(false); setSelectedEmployee(null); }}
                title="Offboard Employee"
                panelClassName="bg-hig-bg-secondary rounded-2xl p-6 w-full max-w-md border border-hig-divider">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                        <LogOut size={24} className="text-red-400" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-hig-text-primary">Offboard Employee</h2>
                        <p className="text-sm text-hig-text-secondary">{selectedEmployee?.name}</p>
                    </div>
                </div>

                <div className="space-y-3 mb-6">
                    <div className="p-3 rounded-xl bg-hig-bg-tertiary border border-hig-divider">
                        <p className="text-sm text-hig-text-primary font-medium mb-2">What happens when you offboard:</p>
                        <ul className="space-y-2 text-xs text-hig-text-secondary">
                            <li className="flex items-start gap-2">
                                <CheckCircle size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                                <span><strong className="text-hig-text-primary">{selectedEmployee?.name}</strong> keeps their replica — ownership is never removed</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <AlertTriangle size={14} className="text-amber-400 mt-0.5 flex-shrink-0" />
                                <span>Replica goes <strong className="text-amber-400">dormant</strong> — company billing stops immediately</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Clock size={14} className="text-hig-text-tertiary mt-0.5 flex-shrink-0" />
                                <span>Employee can reactivate their replica anytime on a personal plan</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Unlink size={14} className="text-red-400 mt-0.5 flex-shrink-0" />
                                <span>Workspace access is revoked — they can no longer see company data</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="flex justify-end gap-3">
                    <button onClick={() => { setShowOffboardModal(false); setSelectedEmployee(null); }}
                        className="px-4 py-2 text-hig-text-secondary hover:text-hig-text-primary text-sm transition-colors">Cancel</button>
                    <button onClick={handleOffboard} disabled={offboardLoading}
                        className="px-5 py-2 bg-red-500 hover:bg-red-400 text-white rounded-xl text-sm font-semibold disabled:opacity-40 transition-colors flex items-center gap-2">
                        {offboardLoading ? <Clock size={14} className="animate-spin" /> : <LogOut size={14} />}
                        {offboardLoading ? 'Processing...' : 'Confirm Offboard'}
                    </button>
                </div>
            </AccessibleModal>

            {/* ══════ Link Replica Modal ══════ */}
            <AccessibleModal open={showLinkModal}
                onClose={() => { setShowLinkModal(false); setSelectedEmployee(null); }}
                title="Link AI Replica"
                panelClassName="bg-hig-bg-secondary rounded-2xl p-6 w-full max-w-lg border border-hig-divider">
                <h2 className="text-xl font-bold text-hig-text-primary mb-1">Link AI Replica</h2>
                <p className="text-sm text-hig-text-secondary mb-1">
                    Choose a replica to pair with <span className="text-cyan-400 font-medium">{selectedEmployee?.name}</span>
                </p>
                <p className="text-xs text-hig-text-tertiary mb-5 flex items-center gap-1.5">
                    <Shield size={12} className="text-emerald-400" />
                    Company will sponsor this replica's costs
                </p>

                <div className="space-y-2 max-h-64 overflow-y-auto">
                    {userReplicas.length === 0 ? (
                        <div className="text-center py-8">
                            <Bot size={32} className="text-hig-text-tertiary mx-auto mb-3" />
                            <p className="text-sm text-hig-text-secondary mb-3">No replicas available</p>
                            <button onClick={() => { setShowLinkModal(false); onNavigate('marketplace'); }}
                                className="text-sm text-cyan-400 hover:underline">Browse Marketplace</button>
                        </div>
                    ) : (
                        userReplicas.map(replica => (
                            <div key={replica.id} className="flex items-center gap-3 p-3 rounded-xl border border-hig-divider hover:border-cyan-500/30 hover:bg-hig-bg-elevated transition-colors">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-sm">
                                    {replica.name.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-hig-text-primary text-sm truncate">{replica.name}</p>
                                    <p className="text-xs text-hig-text-secondary">{replica.category}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleLinkReplica(replica.id, 'AUGMENTED')}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 text-xs font-medium hover:bg-cyan-500/20 transition-colors">
                                        <Zap size={12} /> Augment
                                    </button>
                                    <button onClick={() => handleLinkReplica(replica.id, 'AUTONOMOUS')}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-500/10 text-purple-400 border border-purple-500/20 text-xs font-medium hover:bg-purple-500/20 transition-colors">
                                        <Bot size={12} /> Autonomous
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </AccessibleModal>
        </div>
    );
}
